import { Button } from "@blurb/codex-react";
import { createStyles, MantineProvider, Menu } from "@mantine/core";
import { useModals } from "@mantine/modals";
import { GraphQL } from "@rpi/openapi-api";
import { IconV3, RpiText, showOldNotification as showNotification } from "@rpi/openapi-core";
import React from "react";
import { useOutlet } from "react-router-dom";

import { Pagination } from "../../../components/Pagination";
import { createRpaTableMenuColumn, RpaTableMenu } from "../../../components/RpaTableMenu";
import { RpiTableContainer } from "../../../components/RpiTableContainer";
import { Heading } from "../../../components/Typography";
import { usePageShellStyles } from "../../../components/pageShellStyles";
import { SearchFilterBar } from "../../../components/search-filter/SearchFilterBar";
import { dashboardUserColumns } from "../../../configuration/columns/dashboardUser.column";
import { SearchInput, toSearchInputArray } from "../../../configuration/filters/searchInput";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { useNavigateRoute } from "../../../hooks/useNavigateRoute";
import { useRpiPagination } from "../../../hooks/usePagination";
import { RpiTable } from "../../../rpi-core/table/RpiTable";
import { rpaTheme } from "../../../theme/rpaTheme";
import { SearchGeneral, SearchSort } from "../../../utils/graphql.util";
import { dashboardUserSearchLabels, dashboardUserSearchRenderers } from "../../../utils/searchField.util";
import { DashboardUsersFilter } from "./DashboardUsersFilter";

// Wraps the CTA to its own line at narrow widths and floors the scroll area at 800px.
const useStyles = createStyles({
  rowWrap: { flexWrap: "wrap" },
  scrollFloor: { minWidth: 800 },
});

export const DashboardUsersView: React.FC = () => {
  const { classes, cx } = usePageShellStyles();
  const { classes: localClasses } = useStyles();
  const user = useAuthUser();
  const navigateRoute = useNavigateRoute();
  const outlet = useOutlet();

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.DashboardUsersQueryVariables>>();
  const [searchInputs, setSearchInputs] = React.useState<SearchInput<GraphQL.DashboardUserSearchFields>[]>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.DashboardUsersQueryVariables>>();

  const searchFilters = toSearchInputArray(searchInputs);
  const setSearchFilters = (
    recipe: (
      filters: SearchInput<GraphQL.DashboardUserSearchFields>[]
    ) => SearchInput<GraphQL.DashboardUserSearchFields>[]
  ) => {
    setSearchInputs((prev) => recipe(toSearchInputArray(prev)));
  };

  const pagination = useRpiPagination({ perPage: 20 });

  const [isFilterOpen, setIsFilterOpen] = React.useState(false);

  const handleSearchSubmit = (searchValue: string) => {
    setSearchGeneral(searchValue || undefined);
    pagination.firstPage();
  };

  const { data, isLoading, error, refetch } = GraphQL.useDashboardUsersQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: searchInputs,
        sort: sortInput,
        generalInput: searchGeneral,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.dashboardUsers.totalCount),
    }
  );
  const dashboardUsers = data?.dashboardUsers.data;

  React.useEffect(() => {
    if (outlet === null) refetch();
  }, [outlet]);

  const { mutateAsync: deleteUser, ...deleteUserMutation } = GraphQL.useDeleteDashboardUserMutation();

  const modals = useModals();
  const handleDeleteDashboardUser = React.useCallback(
    (dashboardUser: Pick<GraphQL.DashboardUserResponse, "id" | "email">) => {
      if (deleteUserMutation.isLoading) return;

      return modals.openConfirmModal({
        title: "Delete user",
        centered: true,
        children: <RpiText type="p2" children={`Are you sure you want to delete "${dashboardUser.email}"?`} />,
        labels: { confirm: "Delete", cancel: "Cancel" },
        confirmProps: { color: "red" },
        onConfirm: async () => {
          return await deleteUser({ id: dashboardUser.id })
            .then(() => {
              showNotification({
                title: `Deleted ${dashboardUser.email}.`,
                message: "",
                color: "green",
              });
              refetch();
            })
            .catch((error) => {
              showNotification({
                title: "Failed to delete user.",
                message: error.message,
                color: "red",
              });
            });
        },
      });
    },
    [deleteUser, refetch, deleteUserMutation]
  );

  const canModify = user.is.CUSTOMER_ADMIN || user.is.SUPER_ADMIN;

  return (
    <MantineProvider theme={rpaTheme}>
      <div className={classes.container} data-testid="users-view" aria-label="Users view">
        <Heading as="h1" size="6xl">
          Users
        </Heading>
        <div className={cx(classes.scrollStack, localClasses.scrollFloor)}>
          <div className={cx(classes.row, localClasses.rowWrap)}>
            <div className={classes.searchSlot}>
              <SearchFilterBar
                placeholder="Search by Username or Email"
                isLoading={isLoading}
                filters={searchFilters}
                onSetFilters={setSearchFilters}
                onSearchSubmit={handleSearchSubmit}
                onFilterClick={() => setIsFilterOpen(true)}
                filterLabels={dashboardUserSearchLabels}
                valueRenderers={dashboardUserSearchRenderers}
              />
            </div>
            {canModify && (
              <div className={classes.titleActions}>
                <Button variant="filled" intent="primary" onClick={() => navigateRoute(["dashboard-user:create"])}>
                  <IconV3 icon="add" color="inherit" />
                  Add New User
                </Button>
              </div>
            )}
          </div>
          <RpiTableContainer
            data-testid="dashboard-users-table"
            footer={<Pagination isLoading={isLoading} pagination={pagination} label="Users" />}
          >
            <RpiTable
              emptyMessage={searchInputs?.length || searchGeneral ? "No users found." : "Users will appear here."}
              isLoading={isLoading}
              error={error}
              perPage={dashboardUsers?.length}
              data={dashboardUsers}
              columns={dashboardUserColumns}
              additionalColumns={
                canModify
                  ? [
                      createRpaTableMenuColumn<Pick<GraphQL.DashboardUserResponse, "id" | "email">>((row) => (
                        <RpaTableMenu>
                          <Menu.Item
                            icon={<IconV3 icon="edit" size={20} />}
                            onClick={() => navigateRoute(["dashboard-user:edit", { id: row.id }])}
                          >
                            Edit
                          </Menu.Item>
                          {user.cognitoUser?.attributes?.email !== row.email && (
                            <Menu.Item
                              icon={<IconV3 icon="delete" size={20} color="inherit" />}
                              onClick={() => handleDeleteDashboardUser({ id: row.id, email: row.email })}
                            >
                              Delete
                            </Menu.Item>
                          )}
                        </RpaTableMenu>
                      )),
                    ]
                  : undefined
              }
              withSortBy={{ onChange: setSortInput }}
            />
          </RpiTableContainer>
        </div>
      </div>
      {outlet}
      <DashboardUsersFilter
        isOpened={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filters={searchFilters}
        onApply={setSearchInputs}
      />
    </MantineProvider>
  );
};
